<template>
  <DashboardLayout>
    <div class="space-y-6">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-secondary-900 dark:text-white">System Settings</h1>
          <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Manage company-wide system settings and configurations
          </p>
        </div>
        <button
          @click="showInfo = true"
          class="p-2 text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white rounded-full hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
        >
          <InformationCircleIcon class="h-6 w-6" />
        </button>
      </div>

      <!-- Company Information (Read-only) -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-lg font-semibold text-secondary-900 dark:text-white">Company Information</h2>
          <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Basic company information (read-only)
          </p>
        </div>
        <div class="px-6 py-4 space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300">Company Name</label>
              <p class="mt-1 text-sm text-secondary-900 dark:text-white">{{ company?.name }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300">Company ID</label>
              <p class="mt-1 text-sm text-secondary-900 dark:text-white">{{ company?.id }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300">Subscription Plan</label>
              <p class="mt-1 text-sm text-secondary-900 dark:text-white">{{ company?.subscription_plan || 'Free Trial' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300">Member Since</label>
              <p class="mt-1 text-sm text-secondary-900 dark:text-white">{{ formatDate(company?.created_at) }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300">Total Users</label>
              <p class="mt-1 text-sm text-secondary-900 dark:text-white">{{ company?.users_count || 0 }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Module Management -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-lg font-semibold text-secondary-900 dark:text-white">Module Management</h2>
          <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Enable or disable modules for your organization
          </p>
        </div>
        <div class="px-6 py-4 space-y-4">
          <ModuleToggle
            v-for="module in modules"
            :key="module.id"
            :module="module"
            :enabled="isModuleEnabled(module.id)"
            @toggle="toggleModule"
          />
        </div>
      </div>

      <!-- Regional Settings -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-lg font-semibold text-secondary-900 dark:text-white">Regional Settings</h2>
          <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Configure timezone and formatting preferences
          </p>
        </div>
        <div class="px-6 py-4 space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                Timezone
              </label>
              <select
                v-model="form.timezone"
                class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
              >
                <option v-for="tz in timezones" :key="tz" :value="tz">{{ tz }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                Date Format
              </label>
              <select
                v-model="form.date_format"
                class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
              >
                <option value="MM/DD/YYYY">MM/DD/YYYY (01/31/2025)</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY (31/01/2025)</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD (2025-01-31)</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                Time Format
              </label>
              <select
                v-model="form.time_format"
                class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
              >
                <option value="12h">12-hour (3:30 PM)</option>
                <option value="24h">24-hour (15:30)</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                Currency
              </label>
              <select
                v-model="form.currency"
                class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Security Settings -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-lg font-semibold text-secondary-900 dark:text-white">Security Settings</h2>
          <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Configure security and authentication requirements
          </p>
        </div>
        <div class="px-6 py-4 space-y-4">
          <div class="flex items-center justify-between py-3 border-b border-secondary-200 dark:border-secondary-700 last:border-0">
            <div class="flex-1">
              <h3 class="text-sm font-medium text-secondary-900 dark:text-white">Require Two-Factor Authentication</h3>
              <p class="text-sm text-secondary-600 dark:text-secondary-400">Force all users to enable 2FA</p>
            </div>
            <Toggle v-model="form.require_2fa" />
          </div>
          <div class="flex items-center justify-between py-3 border-b border-secondary-200 dark:border-secondary-700 last:border-0">
            <div class="flex-1">
              <h3 class="text-sm font-medium text-secondary-900 dark:text-white">Strong Password Requirements</h3>
              <p class="text-sm text-secondary-600 dark:text-secondary-400">Enforce complex password rules</p>
            </div>
            <Toggle v-model="form.strong_passwords" />
          </div>
          <div>
            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
              Session Timeout (minutes)
            </label>
            <input
              v-model.number="form.session_timeout"
              type="number"
              min="5"
              max="1440"
              class="w-full md:w-48 px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      <!-- Data Management -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-lg font-semibold text-secondary-900 dark:text-white">Data Management</h2>
          <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Export data and configure retention policies
          </p>
        </div>
        <div class="px-6 py-4 space-y-4">
          <div>
            <h3 class="text-sm font-medium text-secondary-900 dark:text-white mb-2">Export Company Data</h3>
            <p class="text-sm text-secondary-600 dark:text-secondary-400 mb-3">
              Download all company data in CSV format
            </p>
            <button
              @click="exportData"
              :disabled="isExporting"
              class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {{ isExporting ? 'Exporting...' : 'Export Data' }}
            </button>
          </div>
          <div class="border-t border-secondary-200 dark:border-secondary-700 pt-4">
            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
              Data Retention Period (days)
            </label>
            <select
              v-model="form.data_retention_days"
              class="w-full md:w-64 px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
            >
              <option :value="30">30 days</option>
              <option :value="90">90 days</option>
              <option :value="180">180 days</option>
              <option :value="365">1 year</option>
              <option :value="730">2 years</option>
              <option :value="null">Indefinite</option>
            </select>
            <p class="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
              Completed items older than this will be archived
            </p>
          </div>
        </div>
      </div>

      <!-- Support & Feedback -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-lg font-semibold text-secondary-900 dark:text-white">Support & Feedback</h2>
          <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Get help or provide feedback
          </p>
        </div>
        <div class="px-6 py-4 space-y-3">
          <a
            href="/support"
            class="flex items-center justify-between p-3 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors group"
          >
            <div class="flex items-center space-x-3">
              <QuestionMarkCircleIcon class="h-6 w-6 text-secondary-400 dark:text-secondary-500 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
              <div>
                <p class="text-sm font-medium text-secondary-900 dark:text-white">Help Center</p>
                <p class="text-xs text-secondary-600 dark:text-secondary-400">Browse documentation and guides</p>
              </div>
            </div>
            <ChevronRightIcon class="h-5 w-5 text-secondary-400 dark:text-secondary-500" />
          </a>
          <a
            href="/support/contact"
            class="flex items-center justify-between p-3 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors group"
          >
            <div class="flex items-center space-x-3">
              <ChatBubbleLeftRightIcon class="h-6 w-6 text-secondary-400 dark:text-secondary-500 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
              <div>
                <p class="text-sm font-medium text-secondary-900 dark:text-white">Contact Support</p>
                <p class="text-xs text-secondary-600 dark:text-secondary-400">Get help from our support team</p>
              </div>
            </div>
            <ChevronRightIcon class="h-5 w-5 text-secondary-400 dark:text-secondary-500" />
          </a>
          <a
            href="/feedback"
            class="flex items-center justify-between p-3 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors group"
          >
            <div class="flex items-center space-x-3">
              <ChatBubbleBottomCenterTextIcon class="h-6 w-6 text-secondary-400 dark:text-secondary-500 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
              <div>
                <p class="text-sm font-medium text-secondary-900 dark:text-white">Send Feedback</p>
                <p class="text-xs text-secondary-600 dark:text-secondary-400">Share your thoughts and suggestions</p>
              </div>
            </div>
            <ChevronRightIcon class="h-5 w-5 text-secondary-400 dark:text-secondary-500" />
          </a>
        </div>
      </div>

      <!-- Save Button -->
      <div class="flex justify-end">
        <button
          @click="saveSettings"
          :disabled="isSaving"
          class="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {{ isSaving ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
    </div>

    <!-- Info Modal -->
    <TransitionRoot as="template" :show="showInfo">
      <Dialog as="div" class="relative z-50" @close="showInfo = false">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white dark:bg-secondary-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
                    <InformationCircleIcon class="h-6 w-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div class="mt-3 text-center sm:mt-5">
                    <DialogTitle as="h3" class="text-lg font-semibold leading-6 text-secondary-900 dark:text-white">
                      System Settings Guide
                    </DialogTitle>
                    <div class="mt-2">
                      <p class="text-sm text-secondary-600 dark:text-secondary-400 text-left space-y-2">
                        <strong>Module Management:</strong> Enable or disable modules to customize your workspace. Disabled modules won't be visible to users.<br /><br />
                        <strong>Regional Settings:</strong> Configure timezone, date/time formats, and currency for your organization.<br /><br />
                        <strong>Security Settings:</strong> Enforce security requirements like 2FA, strong passwords, and session timeouts.<br /><br />
                        <strong>Data Management:</strong> Export your data or configure how long completed items are retained.<br /><br />
                        Changes are saved immediately when you click "Save Changes".
                      </p>
                    </div>
                  </div>
                </div>
                <div class="mt-5 sm:mt-6">
                  <button
                    type="button"
                    class="inline-flex w-full justify-center rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                    @click="showInfo = false"
                  >
                    Got it, thanks!
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { router } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import ModuleToggle from '@/Components/Settings/ModuleToggle.vue';
import Toggle from '@/Components/Common/Toggle.vue';
import {
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleBottomCenterTextIcon,
  ChevronRightIcon,
} from '@heroicons/vue/24/outline';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';

const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
  settings: {
    type: Object,
    default: () => ({}),
  },
  enabledModules: {
    type: Array,
    default: () => [],
  },
});

const showInfo = ref(false);
const isSaving = ref(false);
const isExporting = ref(false);

const modules = [
  {
    id: 'helpdesk',
    name: 'IT Helpdesk',
    description: 'Ticket management and support system',
    icon: 'TicketIcon',
  },
  {
    id: 'maintenance',
    name: 'Maintenance',
    description: 'Work orders and preventive maintenance',
    icon: 'WrenchScrewdriverIcon',
  },
  {
    id: 'incidents',
    name: 'Incident Management',
    description: 'Track and investigate incidents',
    icon: 'ExclamationTriangleIcon',
  },
];

const timezones = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
];

const form = ref({
  timezone: props.settings.timezone || 'America/New_York',
  date_format: props.settings.date_format || 'MM/DD/YYYY',
  time_format: props.settings.time_format || '12h',
  currency: props.settings.currency || 'USD',
  require_2fa: props.settings.require_2fa || false,
  strong_passwords: props.settings.strong_passwords || true,
  session_timeout: props.settings.session_timeout || 120,
  data_retention_days: props.settings.data_retention_days || 365,
});

const isModuleEnabled = (moduleId) => {
  return props.enabledModules.includes(moduleId);
};

const toggleModule = (moduleId) => {
  router.post('/settings/system/toggle-module', {
    module_id: moduleId,
  }, {
    preserveScroll: true,
    onSuccess: () => {
      // Handle success (e.g., show toast notification)
    },
  });
};

const saveSettings = () => {
  isSaving.value = true;
  router.post('/settings/system', form.value, {
    preserveScroll: true,
    onFinish: () => {
      isSaving.value = false;
    },
    onSuccess: () => {
      // Handle success (e.g., show toast notification)
    },
  });
};

const exportData = () => {
  isExporting.value = true;
  router.post('/settings/system/export', {}, {
    preserveScroll: true,
    onFinish: () => {
      isExporting.value = false;
    },
    onSuccess: () => {
      // Handle success (e.g., show toast notification)
    },
  });
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};
</script>
